<script>
  // The CardCollection shell (ADR-0010): the search page's card list/grid (the
  // want page used it too until its redesign, #222). The page supplies:
  //   - dataSource(query, filters, sort, page) → { items, total }
  //   - config: defaultView, emptyCopy, onCardClick, pageSize
  // The shell owns query/filters/sort/page/view/expandedKeys, the search tools,
  // skeletons, the empty state, and numbered pagination.

  import SearchHeader from '../search/SearchHeader.svelte';
  import SearchFilterPanel from '../search/SearchFilterPanel.svelte';
  import CardRow from './CardRow.svelte';
  import CardTile from './CardTile.svelte';
  import Pagination from './Pagination.svelte';
  import { countActiveFilters, filtersFromParams } from '../../lib/logic/search-logic.js';
  import { debounce } from '../../lib/logic/debounce-utils.js';
  import { SEARCH_RESULTS_PER_PAGE } from '../../lib/logic/config.js';
  import { untrack } from 'svelte';

  const VIEWS = ['list', 'grid'];

  let {
    dataSource,
    defaultView = 'list',
    pageSize = SEARCH_RESULTS_PER_PAGE,
    currencySymbol = '$',
    emptyCopy = 'No cards.',
    searchPlaceholder = 'Search cards across your binders...',
    onCardClick = null,
    // Allow the host page to seed and observe the query/filters/sort/view.
    initialQuery = '',
    initialFilters = null,
    initialSort = 'name-asc',
    onStateChange = null,
    // Changing this resets page→1 and collapses rows WITHOUT remounting the shell,
    // so the user's view/sort survive (search uses it for a group switch; review §8).
    resetKey = null,
    // Autofocus the query box. Search opts in.
    autofocus = false,
  } = $props();

  // The initial* / defaultView props are one-shot seeds (the host observes
  // changes via onStateChange, it never re-seeds), so capturing their initial
  // value is the intent — `untrack` makes that explicit to the compiler.
  let query = $state(untrack(() => initialQuery));
  let sort = $state(untrack(() => initialSort));
  let view = $state(untrack(() => (VIEWS.includes(defaultView) ? defaultView : VIEWS[0])));
  // Clone the seed into each slot so activeFilters and appliedFilters never
  // share structure (a shared reference let an edit to one mutate the other).
  const seedFilters = () =>
    initialFilters ? structuredClone($state.snapshot(initialFilters)) : filtersFromParams({});
  let activeFilters = $state(seedFilters());
  let appliedFilters = $state(seedFilters());
  let filtersOpen = $state(false);
  let lastAppliedSnapshot = $state(untrack(() => JSON.stringify(activeFilters)));

  let page = $state(1);
  let items = $state([]);
  let total = $state(0);
  let isLoading = $state(false);
  let expandedKeys = $state([]);

  const activeFilterCount = $derived(countActiveFilters(appliedFilters));
  const isDirty = $derived(JSON.stringify(activeFilters) !== lastAppliedSnapshot);

  let loadToken = 0;

  async function load() {
    const token = ++loadToken;
    isLoading = true;
    try {
      const result = await dataSource(query, appliedFilters, sort, page);
      if (token !== loadToken) return; // a newer load superseded this one
      items = result?.items || [];
      total = result?.total ?? items.length;
    } catch {
      if (token !== loadToken) return;
      items = [];
      total = 0;
    } finally {
      if (token === loadToken) isLoading = false;
    }
    onStateChange?.({ query, filters: appliedFilters, sort, view, page });
  }

  // Reset page + expansions when the host signals a context switch (e.g. group),
  // but keep view/sort. Skip the initial run so it doesn't clobber a seeded page.
  let prevResetKey = untrack(() => resetKey);
  $effect(() => {
    if (resetKey !== prevResetKey) {
      prevResetKey = resetKey;
      page = 1;
      expandedKeys = [];
    }
  });

  // SINGLE load path: this effect is the ONLY caller of load(). It tracks every
  // input that should trigger a reload (query, sort, appliedFilters, page,
  // resetKey), so the query debounce just assigns `query` + resets the page —
  // it never calls load() itself (that previously double-fired with the
  // effect, plan §6). `resetKey` is tracked directly (not just via `page`)
  // because a `resetKey` change while already on page 1 is a no-op write to
  // `page` — Svelte 5's `$state` setter doesn't mark anything dirty when the
  // value is unchanged, so `load()` would otherwise never re-run on a bare
  // subgroup toggle (regression: search went stale + never re-mirrored the URL).
  $effect(() => {
    void query;
    void sort;
    void appliedFilters;
    void page;
    void resetKey;
    load();
  });

  const debounceQuery = debounce((q) => {
    query = q;
    page = 1;
    expandedKeys = [];
  }, 300);

  function handleInput(q) {
    debounceQuery(q);
  }

  function handleSortChange(mode) {
    sort = mode;
    page = 1;
    expandedKeys = [];
  }

  function handleViewChange(mode) {
    if (!VIEWS.includes(mode)) return;
    view = mode;
    onStateChange?.({ query, filters: appliedFilters, sort, view, page });
  }

  function handleFiltersChange(next) {
    activeFilters = next;
  }

  function handleFiltersApply() {
    appliedFilters = { ...activeFilters };
    lastAppliedSnapshot = JSON.stringify(activeFilters);
    page = 1;
    expandedKeys = [];
  }

  function handlePageChange(p) {
    page = p;
    expandedKeys = [];
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function toggleRow(key) {
    expandedKeys = expandedKeys.includes(key)
      ? expandedKeys.filter((k) => k !== key)
      : [...expandedKeys, key];
  }
</script>

<div class="card-collection">
  <SearchHeader
    viewMode={view}
    sortMode={sort}
    {filtersOpen}
    {activeFilterCount}
    {initialQuery}
    {searchPlaceholder}
    {autofocus}
    onInput={handleInput}
    onViewChange={handleViewChange}
    onSortChange={handleSortChange}
    onToggleFilters={() => (filtersOpen = !filtersOpen)}
  />

  {#if filtersOpen}
    <SearchFilterPanel
      filters={activeFilters}
      {isDirty}
      onFiltersChange={handleFiltersChange}
      onApply={handleFiltersApply}
    />
  {/if}

  <div class="cc-toolbar">
    {#if total > 0}
      <span class="cc-summary">{total} card{total === 1 ? '' : 's'}</span>
    {/if}
  </div>

  <div class="cc-results">
    {#if isLoading && items.length === 0}
      {#if view === 'list'}
        <div class="skeleton-list">
          {#each Array(8) as _, i (i)}
            <div class="skeleton-row">
              <div class="skeleton-line skeleton-name"></div>
              <div class="skeleton-line skeleton-type"></div>
              <div class="skeleton-line skeleton-set"></div>
              <div class="skeleton-line skeleton-price"></div>
            </div>
          {/each}
        </div>
      {:else}
        <div class="skeleton-grid">
          {#each Array(12) as _, i (i)}
            <div class="skeleton-card">
              <div class="skeleton-card-image"></div>
              <div class="skeleton-line skeleton-card-name"></div>
            </div>
          {/each}
        </div>
      {/if}
    {:else if items.length === 0}
      <div class="cc-empty">
        <svg
          viewBox="0 0 24 24"
          width="48"
          height="48"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          opacity="0.4"
        >
          <circle cx="11" cy="11" r="8"></circle>
          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        </svg>
        <p>{emptyCopy}</p>
      </div>
    {:else if view === 'list'}
      <div class="cc-list" class:dim={isLoading}>
        <div class="cc-list-header">
          <span>Name</span>
          <span class="col-meta">Type</span>
          <span class="col-meta">Set</span>
          <span class="col-meta">Rar.</span>
          <span class="col-meta">Owners</span>
          <span class="header-right">Price</span>
        </div>
        {#each items as item (item.key)}
          <CardRow
            {item}
            expanded={expandedKeys.includes(item.key)}
            {currencySymbol}
            {onCardClick}
            onToggle={() => toggleRow(item.key)}
          />
        {/each}
      </div>
    {:else}
      <div class="cc-grid" class:dim={isLoading}>
        {#each items as item (item.key)}
          <CardTile {item} {currencySymbol} {onCardClick} />
        {/each}
      </div>
    {/if}

    {#if !isLoading || items.length > 0}
      <Pagination {page} {total} {pageSize} onPageChange={handlePageChange} />
    {/if}
  </div>
</div>

<style>
  .card-collection {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .cc-toolbar {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem 0;
    min-height: 1.5rem;
  }
  .cc-summary {
    font-size: 0.8125rem;
    color: var(--color-text-tertiary);
  }

  .cc-results {
    padding: 1rem 0;
  }

  .cc-list {
    /* The 6-column track is defined ONCE here and consumed by both the header
       below and CardRow's .cc-row. */
    --cc-grid-cols: minmax(200px, 2fr) 110px 80px 50px minmax(80px, 1fr) 100px;
    background: var(--color-bg-surface);
    border-radius: var(--radius-lg);
    overflow: hidden;
    border: 1px solid var(--color-border);
    transition: opacity 0.15s ease;
  }
  .cc-list-header {
    display: grid;
    grid-template-columns: var(--cc-grid-cols);
    gap: 0.5rem;
    padding: 10px 20px;
    border-bottom: 1px solid var(--color-border);
    font-size: 10px;
    font-family: var(--font-mono);
    color: var(--color-text-muted);
    letter-spacing: 1px;
    text-transform: uppercase;
  }
  .header-right {
    text-align: right;
  }
  /* The list owns its column set: the header's meta cells and CardRow's
     matching cells both read --cc-meta-display, set to none below whenever the
     track drops type / set / rarity / owners. */
  .col-meta {
    display: var(--cc-meta-display, block);
  }

  .cc-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 20px;
    transition: opacity 0.15s ease;
  }

  .dim {
    opacity: 0.5;
    pointer-events: none;
  }

  .cc-empty {
    text-align: center;
    padding: 3rem 1rem;
    color: var(--color-text-placeholder);
  }
  .cc-empty svg {
    margin: 0 auto 1rem;
    display: block;
  }

  /* Skeletons */
  .skeleton-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 0.5rem 0;
  }
  .skeleton-row {
    display: grid;
    grid-template-columns: 2fr 110px 80px 100px;
    gap: 0.75rem;
    align-items: center;
    padding: 0.75rem 0;
    border-bottom: 1px solid var(--color-border);
  }
  .skeleton-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 1rem;
  }
  .skeleton-card {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .skeleton-card-image {
    aspect-ratio: 488 / 680;
    border-radius: 0.5rem;
    background: linear-gradient(
      90deg,
      var(--color-border) 25%,
      var(--color-accent-bg) 50%,
      var(--color-border) 75%
    );
    background-size: 200% 100%;
    animation: cc-shimmer 1.5s ease-in-out infinite;
  }
  .skeleton-line {
    height: 14px;
    border-radius: 4px;
    background: linear-gradient(
      90deg,
      var(--color-border) 25%,
      var(--color-accent-bg) 50%,
      var(--color-border) 75%
    );
    background-size: 200% 100%;
    animation: cc-shimmer 1.5s ease-in-out infinite;
  }
  .skeleton-name {
    width: 70%;
  }
  .skeleton-type {
    width: 80%;
  }
  .skeleton-set {
    width: 60%;
  }
  .skeleton-price {
    width: 60%;
  }
  .skeleton-card-name {
    width: 80%;
    height: 12px;
  }
  @keyframes cc-shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }

  @media (max-width: 768px) {
    .cc-list {
      --cc-meta-display: none;
    }
    .cc-list-header {
      display: none;
    }
    .cc-grid {
      grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
      gap: 12px;
    }
    .skeleton-row {
      grid-template-columns: 1fr 1fr;
    }
  }
</style>
